import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './services/auth.guard';
import { AdminGuard } from './services/admin.guard';
import { CapabilityGuard } from './core/guards/capability.guard';
import { ProfileCompletionGuard } from './services/profile-completion.guard';
import { UserCvPresenceGuard } from './services/user-cv-presence.guard';
import { rootCvUnsavedChangesGuard } from './core/guards/root-cv-unsaved-changes.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  {
    path: 'home',
    // Order matters: UserCvPresenceGuard must run before
    // ProfileCompletionGuard. A brand-new account (no Root CV, no Profile
    // CV, no complete profile) has to land on /home/root-cv/start first -
    // if ProfileCompletionGuard ran first it would bounce that same user to
    // /home/profile before they ever see the Root CV starting point.
    canActivate: [AuthGuard, UserCvPresenceGuard, ProfileCompletionGuard],
    loadComponent: () =>
      import('./components/home/home.component').then((m) => m.HomeComponent),
    children: [
      {
        path: 'profile',
        loadComponent: () =>
          import('./components/profile/profile.component').then(
            (m) => m.ProfileComponent
          ),
      },
      {
        path: 'billing',
        loadComponent: () =>
          import('./components/billing/billing.component').then(
            (m) => m.BillingComponent
          ),
      },
      {
        path: 'plans',
        loadComponent: () =>
          import('./components/plans/plans.component').then(
            (m) => m.PlansComponent
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./components/settings/settings.component').then(
            (m) => m.SettingsComponent
          ),
      },
      {
        path: 'recent-jobs',
        loadComponent: () =>
          import('./components/recent-jobs/recent-jobs.component').then(
            (m) => m.RecentJobsComponent
          ),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./components/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
      },
      {
        path: 'analytics',
        canActivate: [CapabilityGuard],
        data: { capability: 'analytics.view' },
        loadComponent: () =>
          import('./components/analytics-page/analytics-page.component').then(
            (m) => m.AnalyticsPageComponent
          ),
      },
      {
        path: 'batch-tailor',
        canActivate: [CapabilityGuard],
        data: { capability: 'agent.batchTailor' },
        loadComponent: () =>
          import('./components/batch-tailor/batch-tailor-page/batch-tailor-page.component').then(
            (m) => m.BatchTailorPageComponent
          ),
      },
      {
        // Must come before 'batch-tailor/:runId' below - otherwise the
        // router would match "history" as a runId (chamba-ai#383
        // follow-up: run history list for auditability).
        path: 'batch-tailor/history',
        canActivate: [CapabilityGuard],
        data: { capability: 'agent.batchTailor' },
        loadComponent: () =>
          import('./components/batch-tailor/batch-tailor-history/batch-tailor-history.component').then(
            (m) => m.BatchTailorHistoryComponent
          ),
      },
      {
        // Same component as the bare 'batch-tailor' route above - a
        // stable per-run URL (chamba-ai#383 follow-up findings #2/#3/#7/
        // #8) so reload, browser back/forward, and returning in a new
        // tab or after logging back in all reconstruct the same run's
        // state instead of losing it.
        path: 'batch-tailor/:runId',
        canActivate: [CapabilityGuard],
        data: { capability: 'agent.batchTailor' },
        loadComponent: () =>
          import('./components/batch-tailor/batch-tailor-page/batch-tailor-page.component').then(
            (m) => m.BatchTailorPageComponent
          ),
      },
      {
        path: 'user-cvs',
        loadComponent: () =>
          import('./components/user-cvs/user-cvs.component').then(
            (m) => m.UserCvsComponent
          ),
      },
      {
        path: 'user-cvs/:id/versions',
        loadComponent: () =>
          import('./components/user-cvs/user-cv-versions.component').then(
            (m) => m.UserCvVersionsComponent
          ),
      },
      {
        path: 'user-cvs/view',
        loadComponent: () =>
          import('./components/user-cvs/user-cv-viewer.component').then(
            (m) => m.UserCvViewerComponent
          ),
      },
      {
        path: 'root-cv',
        loadComponent: () =>
          import('./components/root-cv/root-cv-list/root-cv-list.component').then(
            (m) => m.RootCvListComponent
          ),
      },
      // Must stay above 'root-cv/:language' - that route would otherwise
      // match 'start' as a language and open the editor (chamba-ai#268).
      {
        path: 'root-cv/start',
        loadComponent: () =>
          import('./components/root-cv/root-cv-start/root-cv-start.component').then(
            (m) => m.RootCvStartComponent
          ),
      },
      {
        path: 'root-cv/:language/review',
        loadComponent: () =>
          import('./components/root-cv/root-cv-review/root-cv-review.component').then(
            (m) => m.RootCvReviewComponent
          ),
      },
      // Must stay above 'root-cv/:language' for the same reason as
      // 'root-cv/start' above (chamba-ai#367).
      {
        path: 'root-cv/:language/translation-review/:jobId',
        loadComponent: () =>
          import('./components/root-cv/root-cv-translation-review/root-cv-translation-review.component').then(
            (m) => m.RootCvTranslationReviewComponent
          ),
      },
      {
        path: 'root-cv/:language',
        loadComponent: () =>
          import('./components/root-cv/root-cv-editor/root-cv-editor.component').then(
            (m) => m.RootCvEditorComponent
          ),
        canDeactivate: [rootCvUnsavedChangesGuard],
      },
      {
        path: 'wizard',
        loadComponent: () =>
          import('./components/job-application-wizard/job-application-wizard.component').then(
            (m) => m.JobApplicationWizardComponent
          ),
      },
      // chamba-ai#268: a separate, self-contained replacement for the
      // wizard's CV+Cover Letter steps - Root CV is now the only CV source,
      // so there's no manual "which CV" choice, and no step navigation.
      // Deliberately its own route, not a change to 'wizard' above, so the
      // existing step-based flow stays exactly as-is for anything still
      // linking to it.
      {
        path: 'quick-application',
        loadComponent: () =>
          import('./components/quick-application/quick-application.component').then(
            (m) => m.QuickApplicationComponent
          ),
      },
      {
        path: 'pipeline-demo',
        loadComponent: () =>
          import('./components/application-pipeline/application-pipeline.component').then(
            (m) => m.ApplicationPipelineComponent
          ),
      },
      {
        path: 'ats-scan',
        loadComponent: () =>
          import('./components/ats-scan/ats-scan-page/ats-scan-page.component').then(
            (m) => m.AtsScanPageComponent
          ),
      },
    ],
  },
  {
    path: 'profile',
    pathMatch: 'full',
    redirectTo: 'home/profile',
  },
  {
    path: 'demo',
    loadComponent: () =>
      import('./components/demo/demo.component').then(
        (m) => m.DemoComponent
      ),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./components/login/login.component').then(
        (m) => m.LoginComponent
      ),
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./components/verify-email/verify-email.component').then(
        (m) => m.VerifyEmailComponent
      ),
  },
  {
    // Public on purpose (chamba-ai#441): opened from the link in a
    // notification email, usually by someone who is not signed in. The
    // signed token in the link is the only credential.
    path: 'unsubscribe',
    loadComponent: () =>
      import('./components/unsubscribe/unsubscribe.component').then(
        (m) => m.UnsubscribeComponent
      ),
  },
  {
    // Browser extension OAuth consent (chamba-ai#420). Outside /home on
    // purpose: only AuthGuard, not the CV-presence/profile-completion
    // guards - connecting the extension must work before either is done.
    path: 'extension/connect',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./components/extension-connect/extension-connect.component').then(
        (m) => m.ExtensionConnectComponent
      ),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./components/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('./components/forbidden/forbidden.component').then(
        (m) => m.ForbiddenComponent
      ),
  },
  {
    path: 'upgrade',
    loadComponent: () =>
      import('./components/upgrade/upgrade.component').then(
        (m) => m.UpgradeComponent
      ),
  },
  {
    path: 'admin',
    canActivate: [AdminGuard],
    loadComponent: () =>
      import('./components/admin/admin-layout/admin-layout.component').then(
        (m) => m.AdminLayoutComponent
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'agents',
      },
      {
        path: 'agents',
        loadComponent: () =>
          import('./components/admin/agents/agents-list/agents-list.component').then(
            (m) => m.AgentsListComponent
          ),
      },
      {
        path: 'agents/new',
        loadComponent: () =>
          import('./components/admin/agents/agent-form/agent-form.component').then(
            (m) => m.AgentFormComponent
          ),
      },
      {
        path: 'agents/:id/edit',
        loadComponent: () =>
          import('./components/admin/agents/agent-form/agent-form.component').then(
            (m) => m.AgentFormComponent
          ),
      },
      {
        path: 'prompts',
        loadComponent: () =>
          import('./components/admin/prompts/prompts-list/prompts-list.component').then(
            (m) => m.PromptsListComponent
          ),
      },
      {
        path: 'prompts/new',
        loadComponent: () =>
          import('./components/admin/prompts/prompt-form/prompt-form.component').then(
            (m) => m.PromptFormComponent
          ),
      },
      {
        path: 'prompts/:id/edit',
        loadComponent: () =>
          import('./components/admin/prompts/prompt-form/prompt-form.component').then(
            (m) => m.PromptFormComponent
          ),
      },
      {
        path: 'prompts/:id/versions',
        loadComponent: () =>
          import('./components/admin/prompts/prompt-versions/prompt-versions.component').then(
            (m) => m.PromptVersionsComponent
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./components/admin/users/users-list/users-list.component').then(
            (m) => m.UsersListComponent
          ),
      },
      {
        path: 'users/new',
        loadComponent: () =>
          import('./components/admin/users/user-form/user-form.component').then(
            (m) => m.UserFormComponent
          ),
      },
      {
        path: 'users/:id',
        loadComponent: () =>
          import('./components/admin/users/user-detail/user-detail.component').then(
            (m) => m.UserDetailComponent
          ),
      },
      {
        path: 'users/:id/edit',
        loadComponent: () =>
          import('./components/admin/users/user-detail/user-detail.component').then(
            (m) => m.UserDetailComponent
          ),
      },
      {
        path: 'plans',
        loadComponent: () =>
          import('./components/admin/plans/plans.component').then(
            (m) => m.AdminPlansComponent
          ),
      },
      {
        path: 'fair-use',
        loadComponent: () =>
          import('./components/admin/fair-use/fair-use.component').then(
            (m) => m.AdminFairUseComponent
          ),
      },
      {
        path: 'programs',
        loadComponent: () =>
          import('./components/admin/programs/programs.component').then(
            (m) => m.AdminProgramsComponent
          ),
      },
      {
        path: 'system-status',
        loadComponent: () =>
          import('./components/admin/system-status/system-status.component').then(
            (m) => m.SystemStatusComponent
          ),
      },
      {
        path: 'exports',
        loadComponent: () =>
          import('./components/admin/exports/exports.component').then(
            (m) => m.ExportsComponent
          ),
      },
      {
        path: 'llm-providers',
        loadComponent: () =>
          import('./components/admin/llm-providers/llm-providers-list/llm-providers-list.component').then(
            (m) => m.LLMProvidersListComponent
          ),
      },
      {
        path: 'llm-providers/new',
        loadComponent: () =>
          import('./components/admin/llm-providers/llm-provider-form/llm-provider-form.component').then(
            (m) => m.LLMProviderFormComponent
          ),
      },
      {
        path: 'llm-providers/:id/edit',
        loadComponent: () =>
          import('./components/admin/llm-providers/llm-provider-form/llm-provider-form.component').then(
            (m) => m.LLMProviderFormComponent
          ),
      },
      {
        path: 'llm-models',
        loadComponent: () =>
          import('./components/admin/llm-models/llm-models-list/llm-models-list.component').then(
            (m) => m.LLMModelsListComponent
          ),
      },
      {
        path: 'llm-models/new',
        loadComponent: () =>
          import('./components/admin/llm-models/llm-model-form/llm-model-form.component').then(
            (m) => m.LLMModelFormComponent
          ),
      },
      {
        path: 'llm-models/:id/edit',
        loadComponent: () =>
          import('./components/admin/llm-models/llm-model-form/llm-model-form.component').then(
            (m) => m.LLMModelFormComponent
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
